import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, Edit2, Shield, Eye, UserCog } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/hooks/useAuth';

const ROLE_CONFIG = {
  administrator: {
    icon: Shield,
    label: 'Administrator',
    description: 'Full access + user management',
    color: 'text-purple-600'
  },
  manager: {
    icon: UserCog,
    label: 'Manager',
    description: 'Can manage appointments & teams',
    color: 'text-blue-600'
  },
  viewer: {
    icon: Eye,
    label: 'Viewer',
    description: 'Read-only access',
    color: 'text-slate-600'
  }
};

export default function UserManagementDialog({ open, onClose, currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'viewer'
  });

  useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/users', formData);
      toast.success('User created successfully');
      setFormData({ name: '', email: '', password: '', role: 'viewer' });
      setIsAdding(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (userId) => {
    setLoading(true);
    try {
      const updateData = { name: formData.name, role: formData.role };
      if (formData.password) {
        updateData.password = formData.password;
      }
      await api.put(`/users/${userId}`, updateData);
      toast.success('User updated successfully');
      setFormData({ name: '', email: '', password: '', role: 'viewer' });
      setEditingId(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(`/users/${userId}`);
        toast.success('User deleted successfully');
        fetchUsers();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete user');
      }
    }
  };

  const startEdit = (user) => {
    setEditingId(user.id);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role
    });
    setIsAdding(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setIsAdding(false);
    setFormData({ name: '', email: '', password: '', role: 'viewer' });
  };

  const RoleIcon = ({ role }) => {
    const config = ROLE_CONFIG[role] || ROLE_CONFIG.viewer;
    const Icon = config.icon;
    return <Icon size={16} className={config.color} />;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px]" data-testid="user-management-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#0f172a] flex items-center gap-2">
            <Shield size={24} />
            User Management
          </DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Users List */}
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {users.length === 0 && !isAdding && (
              <div className="text-center py-8 text-slate-500">
                <Shield size={48} className="mx-auto mb-2 opacity-50" />
                <p>No users found</p>
              </div>
            )}

            {users.map((user) => {
              const roleConfig = ROLE_CONFIG[user.role] || ROLE_CONFIG.viewer;
              return (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-4 border border-slate-200 rounded-lg bg-white hover:bg-slate-50"
                  data-testid={`user-${user.id}`}
                >
                  {editingId === user.id ? (
                    <div className="flex-1 space-y-2">
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Name"
                        data-testid="edit-user-name-input"
                      />
                      <select
                        value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        data-testid="edit-user-role-select"
                      >
                        <option value="administrator">Administrator - Full Access</option>
                        <option value="manager">Manager - Manage Appointments</option>
                        <option value="viewer">Viewer - Read Only</option>
                      </select>
                      <Input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="New Password (optional)"
                        data-testid="edit-user-password-input"
                      />
                      <div className="flex gap-2 mt-2">
                        <Button
                          size="sm"
                          onClick={() => handleUpdate(user.id)}
                          disabled={loading}
                          className="bg-[#2563eb] hover:bg-[#1d4ed8]"
                          data-testid="save-user-button"
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={cancelEdit}
                          data-testid="cancel-edit-user-button"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-[#0f172a]" data-testid="user-name">{user.name}</p>
                          {user.id === currentUser?.id && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">You</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600" data-testid="user-email">{user.email}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <RoleIcon role={user.role} />
                          <span className="text-sm font-medium" data-testid="user-role">
                            {roleConfig.label}
                          </span>
                          <span className="text-xs text-slate-500">- {roleConfig.description}</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => startEdit(user)}
                          data-testid={`edit-user-${user.id}-button`}
                        >
                          <Edit2 size={16} />
                        </Button>
                        {user.id !== currentUser?.id && (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleDelete(user.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-user-${user.id}-button`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}

            {/* Add New Form */}
            {isAdding && (
              <form onSubmit={handleAdd} className="p-4 border-2 border-dashed border-[#2563eb] rounded-lg bg-blue-50/50">
                <div className="space-y-2">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Doe"
                      required
                      data-testid="add-user-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@company.com"
                      required
                      data-testid="add-user-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="password">Password *</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                      required
                      data-testid="add-user-password-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="role">Role *</Label>
                    <select
                      id="role"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="add-user-role-select"
                    >
                      <option value="viewer">Viewer - Read Only Access</option>
                      <option value="manager">Manager - Can Manage Appointments</option>
                      <option value="administrator">Administrator - Full Access</option>
                    </select>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="bg-[#2563eb] hover:bg-[#1d4ed8]"
                      data-testid="submit-add-user-button"
                    >
                      {loading ? 'Creating...' : 'Create User'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={cancelEdit}
                      data-testid="cancel-add-user-button"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </form>
            )}
          </div>

          {/* Add Button */}
          {!isAdding && !editingId && (
            <Button
              variant="outline"
              className="w-full border-dashed border-2 border-[#2563eb] text-[#2563eb] hover:bg-blue-50"
              onClick={() => setIsAdding(true)}
              data-testid="add-user-button"
            >
              <Plus size={20} className="mr-2" />
              Add New User
            </Button>
          )}
        </div>

        <DialogFooter>
          <Button onClick={onClose} data-testid="close-user-management-button">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
